import { useCallback, useMemo, useState } from 'react';
import { Tile, TileHeader, TileTabsRow } from '@creditflowresearch/uilib/Tile';
import Title from '@creditflowresearch/uilib/Title';
import { useCorporateBondScreenerContext } from '@/creditflowresearch/application/CorporateBondScreener/CorporateBondScreenerContext';
import { useCorporateBondScreenerMySelectionContext } from '@/creditflowresearch/application/CorporateBondScreener/CorporateBondScreenerMySelectionContext';
import {
  CorporateBondScreenerSidebarFilterProvider,
} from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/CorporateBondScreenerSidebarFilterContext';
import type {
  IApplyOptions,
} from '@/creditflowresearch/application/BondScreener/Sidebar/createUseBondScreenerSidebarFilter';
import {
  ColumnsCustomizationProvider,
} from '@/creditflowresearch/application/BondScreener/Sidebar/useColumnsCustomizationContext';
import {
  CorporateBondScreenerViewsProvider,
} from '@/creditflowresearch/application/CorporateBondScreener/Sidebar/CorporateBondScreenerViewsProvider';
import {
  useBondScreenerColumnsContext,
} from '@/creditflowresearch/application/BondScreener/BondScreenerColumnsContext';
import {
  LOCKED_COLUMN_IDS,
} from '@/creditflowresearch/ui/components/corporate-bond-screener/Table/columns/columnRendererRegistry';
import type {
  IBondScreenerViewsResponse,
  IFilterBoundaryRegion,
  TFilterPayload,
} from '@/creditflowresearch/domain/vo/Bondscreener';
import { BondScreenerTabBar } from '@/creditflowresearch/ui/common/components/molecules/BondScreenerTabBar';
import { ScreenerTab } from '@/creditflowresearch/ui/components/corporate-bond-screener/Tabs/ScreenerTab';
import { MySelectionTab } from '@/creditflowresearch/ui/components/corporate-bond-screener/Tabs/MySelectionTab';
import {
  CorporateBondScreenerSidebarPanel,
} from '@/creditflowresearch/ui/components/corporate-bond-screener/Sidebar/CorporateBondScreenerSidebarPanel';
import CorporateBondScreenerHeader from './CorporateBondScreenerHeader';
import {
  CORPORATE_BS_TABS_CONFIG,
  CORPORATE_BOND_TAB,
  type CorporateBondTabType,
} from './corporateBondScreenerTabsConfig';

import styles from '@/creditflowresearch/ui/components/bondscreener/bondscreener.module.scss';

const TAB_CONTENT = {
  [CORPORATE_BOND_TAB.SCREENER]: ScreenerTab,
  [CORPORATE_BOND_TAB.MY_SELECTION]: MySelectionTab,
};

interface ICorporateBondScreenerViewProps {
  initialViews: IBondScreenerViewsResponse<TFilterPayload>,
}

const CorporateBondScreenerView = ({ initialViews }: ICorporateBondScreenerViewProps) => {
  const [activeTabId, setActiveTabId] = useState<CorporateBondTabType>(CORPORATE_BOND_TAB.SCREENER);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const { filterPayload, setFilterPayload } = useCorporateBondScreenerContext();
  const { selectionCount } = useCorporateBondScreenerMySelectionContext();
  const { allColumns, activeColumnsIds, defaultColumnsIds, setActiveColumnsIds } = useBondScreenerColumnsContext();

  const activeTab = CORPORATE_BS_TABS_CONFIG[activeTabId];
  const TabContent = TAB_CONTENT[activeTabId];

  const tabs = useMemo(() => Object.values(CORPORATE_BS_TABS_CONFIG).map((tab) => ({
    ...tab,
    badge: tab.hasBadge ? selectionCount : undefined,
  })), [selectionCount]);

  const openSidebar = useCallback((): void => setIsSidebarOpen(true), []);
  const closeSidebar = useCallback((): void => setIsSidebarOpen(false), []);

  const handleApply = useCallback((
    filters: TFilterPayload,
    boundaryRegions: IFilterBoundaryRegion[],
    options?: IApplyOptions,
  ): void => {
    setFilterPayload(filters);
    if (!options?.skipClose) {
      closeSidebar();
    }
  }, [setFilterPayload, closeSidebar]);

  return (
    <CorporateBondScreenerSidebarFilterProvider
      isOpen={ isSidebarOpen }
      payload={ filterPayload }
      onApply={ handleApply }
    >
      <ColumnsCustomizationProvider
        allColumns={ allColumns }
        activeColumnsIds={ activeColumnsIds }
        defaultColumnsIds={ defaultColumnsIds }
        lockedColumnIds={ LOCKED_COLUMN_IDS }
        onApply={ setActiveColumnsIds }
      >
        <CorporateBondScreenerViewsProvider type="corp" initialViews={ initialViews }>
          <Tile dataTest="corporateBondScreener" className={ styles.tile }>
            <TileHeader
              title={ <Title title="Bond Screener" /> }
              noDesktopBottomBorder
              hideTitleOnDesktop
              className={ styles.tileHeader }
              dataTest="corporateBondScreenerHeader"
            >
              <CorporateBondScreenerHeader
                activeTabId={ activeTabId }
                hasSearch={ activeTab?.hasSearch ?? false }
                onTabChange={ setActiveTabId }
                onOpenSidebar={ openSidebar }
              />
            </TileHeader>

            <TileTabsRow dataTest="corporateBondScreenerTabs">
              <BondScreenerTabBar
                dataTest="corporateBondScreenerTab"
                tabs={ tabs }
                activeTabId={ activeTabId }
                onTabChange={ (id) => setActiveTabId(id as CorporateBondTabType) }
              />
            </TileTabsRow>

            { TabContent && <TabContent /> }
          </Tile>

          <CorporateBondScreenerSidebarPanel isOpen={ isSidebarOpen } onClose={ closeSidebar } />
        </CorporateBondScreenerViewsProvider>
      </ColumnsCustomizationProvider>
    </CorporateBondScreenerSidebarFilterProvider>
  );
};

export default CorporateBondScreenerView;
